import { useState, useEffect, useCallback } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import { Database, MagnifyingGlass, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { getApiUrl, authFetch } from '../config';
import SubPageHeader from '../components/SubPageHeader';

interface WpoolRecord {
  id: number;
  rid: number;
  akn: string;
  ttl: string;
  vnm: string;
  nnm: string;
  str: string;
  hnr: string;
  plz: string;
  ort: string;
  lkn: string;
  gjj: number;
  gmm: string;
  gtt: string;
  hkn: string;
  hkd: string;
  lfr: string;
  chk: string;
  akt: string;
  tns: string;
  ema: string;
  inf3: string;
}

interface Statistics {
  total: number;
  byHkn: { hkn: string; count: number }[];
  byChk: { chk: string; count: number }[];
  byLfr: { lfr: string; count: number }[];
}

const CHK_LABELS: Record<string, string> = {
  ' ': 'Offen',
  'X': 'Gesperrt',
  'S': 'Selektiert',
  'D': 'Dublette',
};

const CHK_COLORS: Record<string, string> = {
  ' ': '#2563eb',
  'X': '#dc2626',
  'S': '#059669',
  'D': '#d97706',
};

export default function WpoolView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [records, setRecords] = useState<WpoolRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState('');
  const [filterHkn, setFilterHkn] = useState('');
  const [filterChk, setFilterChk] = useState('');
  const [filterLfr, setFilterLfr] = useState('');

  const [stats, setStats] = useState<Statistics | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const totalPages = Math.ceil(total / limit);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search) params.set('search', search);
      if (filterHkn) params.set('hkn', filterHkn);
      if (filterChk) params.set('chk', filterChk);
      if (filterLfr) params.set('lfr', filterLfr);

      const res = await authFetch(getApiUrl(`/wpool?${params.toString()}`));
      const data = await res.json();
      setRecords(data.records || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, limit, search, filterHkn, filterChk, filterLfr]);

  const loadStatistics = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await authFetch(getApiUrl('/wpool/statistics'));
      const data = await res.json();
      setStats(data);
    } catch { /* ignore */ }
    setLoadingStats(false);
  }, []);

  useEffect(() => { loadRecords(); }, [loadRecords]);
  useEffect(() => { loadStatistics(); }, [loadStatistics]);

  const handleSearch = () => {
    setPage(1);
    loadRecords();
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
      <SubPageHeader title="WPool Daten" />

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem' }}>
        <h1 style={{ color: '#003366', margin: '0 0 1.5rem 0', fontSize: isMobile ? '1.5rem' : '2rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Database size={28} weight="fill" /> WPool Daten
        </h1>

        {/* Statistik-Karten */}
        {stats && !loadingStats && (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            {/* Total */}
            <div style={{
              backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
            }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Gesamt</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#003366' }}>
                {stats.total.toLocaleString()}
              </div>
            </div>

            {/* CHK Status */}
            <div style={{
              backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
            }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>CHK Status</div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                {stats.byChk.map(c => (
                  <div key={c.chk || 'null'}>
                    <span style={{
                      fontWeight: 700, fontSize: '1rem',
                      color: CHK_COLORS[c.chk] || '#6b7280',
                    }}>
                      {Number(c.count).toLocaleString()}
                    </span>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.25rem' }}>
                      {CHK_LABELS[c.chk] || c.chk || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top HKN */}
            <div style={{
              backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
            }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Top Herkunft (HKN)</div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                {stats.byHkn.slice(0, 5).map(h => (
                  <div key={h.hkn || 'null'} style={{ fontSize: '0.8rem' }}>
                    <span style={{ fontWeight: 700, color: '#003366' }}>
                      {Number(h.count).toLocaleString()}
                    </span>
                    <span style={{ color: '#6b7280', marginLeft: '0.2rem', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {h.hkn || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top LFR */}
            <div style={{
              backgroundColor: 'white', padding: '1.25rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
            }}>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Top Lieferant (LFR)</div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                {stats.byLfr.slice(0, 5).map(l => (
                  <div key={l.lfr || 'null'} style={{ fontSize: '0.8rem' }}>
                    <span style={{ fontWeight: 700, color: '#003366' }}>
                      {Number(l.count).toLocaleString()}
                    </span>
                    <span style={{ color: '#6b7280', marginLeft: '0.2rem', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {l.lfr || '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Filter-Leiste */}
        <div style={{
          backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
          marginBottom: '1.5rem',
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>Suche (Name/PLZ/Ort/Str.)</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="text"
                  placeholder="Name, PLZ, Ort, Stra\u00dfe..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                  style={{ flex: 1, padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
                />
                <button
                  onClick={handleSearch}
                  style={{ padding: '0.5rem 0.75rem', backgroundColor: '#003366', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                >
                  <MagnifyingGlass size={16} weight="bold" />
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>HKN (Herkunft)</label>
              <input
                type="text"
                placeholder="z.B. BOE04216"
                value={filterHkn}
                onChange={e => { setFilterHkn(e.target.value); setPage(1); }}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '120px' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>CHK Status</label>
              <select
                value={filterChk}
                onChange={e => { setFilterChk(e.target.value); setPage(1); }}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem' }}
              >
                <option value="">Alle</option>
                <option value=" ">Offen</option>
                <option value="X">Gesperrt</option>
                <option value="S">Selektiert</option>
                <option value="D">Dublette</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '0.8rem', color: '#6b7280', marginBottom: '0.25rem' }}>LFR (Lieferant)</label>
              <input
                type="text"
                placeholder="z.B. BOE"
                value={filterLfr}
                onChange={e => { setFilterLfr(e.target.value); setPage(1); }}
                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                style={{ padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.85rem', width: '100px' }}
              />
            </div>
          </div>
        </div>

        {/* Datentabelle */}
        <div style={{
          backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb',
        }}>
          {/* Header mit Pagination */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ color: '#6b7280', fontSize: '0.85rem' }}>
              {loading ? 'Lade...' : `${total.toLocaleString()} Ergebnisse`}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
              >
                <CaretLeft size={16} />
              </button>
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {page} / {totalPages || 1}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
              >
                <CaretRight size={16} />
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'center' }}>CHK</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Name</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Stra&szlig;e</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>PLZ/Ort</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Geb.</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>HKN</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>LFR</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>RID</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>Telefon</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>E-Mail</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left' }}>INF3</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                      <span style={{
                        padding: '1px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600,
                        backgroundColor: r.chk === 'S' ? '#dcfce7' : r.chk === 'X' ? '#fecaca' : r.chk === 'D' ? '#fef3c7' : '#dbeafe',
                        color: r.chk === 'S' ? '#166534' : r.chk === 'X' ? '#991b1b' : r.chk === 'D' ? '#92400e' : '#1e40af',
                      }}>
                        {CHK_LABELS[r.chk] || r.chk || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', whiteSpace: 'nowrap' }}>
                      {r.ttl ? <span style={{ color: '#6b7280', marginRight: '0.25rem' }}>{r.ttl}</span> : null}
                      {r.vnm} {r.nnm}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{[r.str, r.hnr].filter(Boolean).join(' ') || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{r.plz} {r.ort}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{r.gjj && r.gmm && r.gtt ? `${r.gtt}.${r.gmm}.${r.gjj}` : '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.hkn || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.lfr || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.rid || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280' }}>{r.tns || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', color: '#6b7280', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.ema || '-'}</td>
                    <td style={{ padding: '0.4rem 0.5rem', fontFamily: 'monospace', fontSize: '0.7rem', color: '#6b7280', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.inf3 || '-'}</td>
                  </tr>
                ))}
                {records.length === 0 && !loading && (
                  <tr><td colSpan={11} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', fontStyle: 'italic' }}>Keine Daten gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination unten */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? 0.5 : 1 }}
              >
                <CaretLeft size={16} />
              </button>
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Seite {page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: '4px', backgroundColor: 'white', cursor: page >= totalPages ? 'not-allowed' : 'pointer', opacity: page >= totalPages ? 0.5 : 1 }}
              >
                <CaretRight size={16} />
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
